import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { useAsyncData } from '@/hooks';
import { projectApi } from '@/services/projects/projectApi';
import type { CrmProject, DevProject, ProjectSource, ProjectDashboardStats } from '@/types/projects';
import {
  ArrowLeftIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { PROJECT_STATUSES } from '@/types/projects';

interface Props {
  source: ProjectSource;
  backRoute: string;
  detailRoutePrefix: string;
  titleKey: string;
  canCreate?: boolean;
}

const statusColors: Record<string, string> = {
  planned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  on_hold: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  completed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

export const ProjectListPage: React.FC<Props> = ({
  source, backRoute, detailRoutePrefix, titleKey, canCreate = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formCustomerId, setFormCustomerId] = useState('');

  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (statusFilter) params.status = statusFilter;

  const { data: projects, loading } = useAsyncData<(CrmProject | DevProject)[]>(
    source === 'crm' ? '/api/crm/projects' : '/api/admin/dev-projects',
    { params, deps: [search, statusFilter], initialData: [] }
  );

  const { data: stats } = useAsyncData<ProjectDashboardStats>(
    source === 'crm' ? '/api/crm/projects/stats' : '/api/admin/dev-projects/stats'
  );

  const { data: customers } = useAsyncData<{ id: string; name: string }[]>(
    '/api/crm/customers',
    { initialData: [], skip: source !== 'crm' }
  );

  const projectList = projects || [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;
    const payload = source === 'crm'
      ? { name: formName, customer_id: formCustomerId, status: 'planned' }
      : { name: formName, status: 'planned' };
    const created = await projectApi.create(source, payload);
    setShowForm(false);
    setFormName('');
    navigate(`${detailRoutePrefix}/${(created as any).id}`);
  };

  return (
    <div className="p-4 bg-bgContainer h-full overflow-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(backRoute)} className="p-2 hover:bg-bgElevated rounded-lg">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <FolderIcon className="w-6 h-6 text-primary" />
            {t(titleKey)}
          </h1>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg"
          >
            <PlusIcon className="w-5 h-5" />
            {t('projects.newProject')}
          </button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard label={t('projects.stats.total')} value={stats.total} />
          <StatCard label={t('projects.stats.active')} value={stats.active} color="text-green-600" />
          <StatCard label={t('projects.stats.myTasks')} value={stats.my_tasks} />
          <StatCard label={t('projects.stats.overdue')} value={stats.overdue_tasks} color="text-red-600" />
          <StatCard label={t('projects.stats.hoursWeek')} value={`${stats.hours_this_week}h`} />
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-textSecondary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('projects.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bgBase"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-border rounded-lg bg-bgBase"
        >
          <option value="">{t('projects.allStatuses')}</option>
          {PROJECT_STATUSES.map((s) => (
            <option key={s} value={s}>{t(`projects.status.${s}`)}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-textSecondary">{t('common.loading')}</p>
      ) : (
        <div className="space-y-2">
          {(projectList as (CrmProject | DevProject)[]).map((p) => (
            <Link
              key={p.id}
              to={`${detailRoutePrefix}/${p.id}`}
              className="block p-4 bg-bgElevated rounded-lg border border-border hover:border-primary transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-textDefault">{p.name}</h3>
                  <p className="text-sm text-textSecondary">
                    {source === 'crm' && (p as CrmProject).customer_name}
                    {source === 'dev' && (p as DevProject).release_target && ` · ${(p as DevProject).release_target}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded text-xs ${statusColors[p.status]}`}>
                    {t(`projects.status.${p.status}`)}
                  </span>
                  {(p as any).open_tasks > 0 && (
                    <span className="text-xs text-textSecondary">
                      {((p as any).open_tasks)} {t('projects.openTasks')}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
          {!projectList.length && (
            <p className="text-center text-textSecondary py-8">{t('projects.noProjects')}</p>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form onSubmit={handleCreate} className="bg-bgContainer p-6 rounded-lg w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold">{t('projects.newProject')}</h2>
            <input
              required
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder={t('projects.projectName')}
              className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase"
            />
            {source === 'crm' && (
              <select
                required
                value={formCustomerId}
                onChange={(e) => setFormCustomerId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase"
              >
                <option value="">{t('projects.selectCustomer')}</option>
                {(customers || []).map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg">
                {t('common.cancel')}
              </button>
              <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg">
                {t('common.create')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

function StatCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="p-3 bg-bgElevated rounded-lg border border-border">
      <div className={`text-xl font-bold ${color || 'text-textDefault'}`}>{value}</div>
      <div className="text-xs text-textSecondary">{label}</div>
    </div>
  );
}
